/**
 * What to call a unit of each product (#430).
 *
 * Asked for with the scope call: "full chain for all 3 but with size
 * names appropriate for the item. Not all of them use bottles."
 *
 * ── WHAT THIS IS AND IS NOT ──
 *
 * It is NOT the size names. Those are the keeper's own free text in
 * `app.BottleSizes.label` -- "4 oz bar", "1 oz tincture" -- and nothing
 * here overrides them. What this fixes is the surrounding CHROME, which
 * said "jar" and "bottle" regardless: a column header, a count, the verb
 * on a button. Calling a wax block a jar is the app telling somebody it
 * does not understand what they sell.
 *
 * ── ONE MODULE, BECAUSE THESE WILL BE WRONG ──
 *
 * The nouns below are a considered starting point, not domain truth. Wax
 * is sold as blocks and as bars; pollen goes into jars, pouches and bags
 * depending who you ask. Rather than scatter the words through two
 * clients and then hunt them down, every one lives here -- so correcting
 * "block" to "bar" is a single edit that both platforms inherit.
 *
 * The verbs are the half worth getting right, because they are actions
 * rather than labels. Wax is POURED into moulds; propolis tincture is
 * genuinely BOTTLED; pollen is PACKED. "Bottle this wax" is the kind of
 * wrong that makes a keeper distrust the rest of the screen.
 *
 * MIRRORS mobile/src/services/productVocabulary.js, registered as a
 * parity pair. Two clients disagreeing about what a unit of wax is
 * called would each look correct on their own screen.
 */

/* Honey keeps the words it has always used. Nothing about the existing
   pool and bottling screens changes, which is the point: this exists to
   stop the OTHER products borrowing honey's vocabulary, not to rename
   honey's. */
const VOCABULARY = {
  Honey: {
    unit: "jar", units: "jars", verb: "Bottle", raw: "unbottled",
    entryUnit: "lb", example: "1 lb, 12 oz, honey bear",
  },
  Wax: {
    unit: "block", units: "blocks", verb: "Pour", raw: "unpoured",
    /* "block by weight is fine" -- confirmed 4 Oct. A block is named and
       sold by what it weighs, so pounds is the number the seller already
       has in mind. */
    entryUnit: "lb", example: "1 lb block, 4 oz bar",
  },
  Propolis: {
    unit: "tincture", units: "tinctures", verb: "Bottle", raw: "unbottled",
    /* Tinctures are small enough that pounds would be all zeroes --
       a 1 oz bottle is 0.0625 lb, which is a number nobody types. */
    entryUnit: "oz", example: "1 oz tincture, 2 oz dropper",
  },
  Pollen: {
    unit: "jar", units: "jars", verb: "Pack", raw: "unpacked",
    /* "pouch should be jar by ounce" -- confirmed 4 Oct, replacing the
       pouch this module shipped with. Pollen shares honey's container
       and not its scale: jars of pollen are sold in ounces, so the size
       is entered in ounces even though the pool is still kept in pounds.

       SHARING A NOUN WITH HONEY IS WHY `example` EXISTS. The add-size
       dialog used to pick its example by asking whether the unit was a
       jar, which was fine while only honey had one -- it would now offer
       "honey bear" on a pollen screen. A product's words belong to the
       product, not to whatever else happens to use the same container. */
    entryUnit: "oz", example: "4 oz, 8 oz",
  },
};

/* Sizes are STORED in pounds whatever they are entered in. The pool is
   kept in pounds, every movement is in pounds, and V120 widened the
   column to DECIMAL(12,4) precisely so an ounce converts exactly rather
   than being rounded -- 1 oz is 0.0625 lb, held as 0.0625 and not 0.063.

   Converting at the edge rather than storing a unit per row: a column
   saying which unit each number is in would mean every reader has to
   check it, and the first one that forgets does arithmetic across two
   scales. The database has one scale. */
const OUNCES_PER_POUND = 16;

/** Entered weight -> pounds, for storage. */
export function toPounds(productType, amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return NaN;
  return vocabularyFor(productType).entryUnit === "oz" ? n / OUNCES_PER_POUND : n;
}

/** Stored pounds -> the unit this product is entered and read in. */
export function fromPounds(productType, pounds) {
  const n = Number(pounds);
  if (!Number.isFinite(n)) return NaN;
  return vocabularyFor(productType).entryUnit === "oz" ? n * OUNCES_PER_POUND : n;
}

/**
 * "4 oz", "1.5 lb" -- a weight in the unit the product is sold by.
 *
 * TRAILING ZEROES TRIMMED, because the stored number is a conversion
 * artefact as often as a real figure: 4 oz round-trips through pounds as
 * 4.0000, and "4.0000 oz" on a shelf list reads as a precision nobody
 * claimed.
 */
export function weightLabel(productType, pounds) {
  const v = vocabularyFor(productType);
  const n = fromPounds(productType, pounds);
  if (!Number.isFinite(n)) return "";
  const text = n.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
  return `${text} ${v.entryUnit}`;
}

/* The fallback is deliberately NEUTRAL rather than honey's.
 *
 * A product this module has not met is far likelier to be a new one than
 * to be honey, and "unit" is never actively wrong -- while calling an
 * unknown product's container a jar is. V120's CHECK constraints mean
 * only four values can exist today, so this is unreachable now; it is
 * here so that adding a fifth product to the schema and forgetting this
 * file degrades to something bland instead of something false. */
const NEUTRAL = { unit: "unit", units: "units", verb: "Package", raw: "unpackaged" };

export function vocabularyFor(productType) {
  return VOCABULARY[productType] || NEUTRAL;
}

/** "12 jars", "1 block" — the count with the right noun on it. */
export function countOfUnits(productType, count) {
  const v = vocabularyFor(productType);
  const n = Number(count);
  return `${n} ${Math.abs(n) === 1 ? v.unit : v.units}`;
}

/* Exported for the parity test and for anything that needs to know the
   full set without importing the object. */
export const KNOWN_PRODUCTS = Object.keys(VOCABULARY);
